import { useState } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { ChartPoint } from "../types";

type View = "profit" | "roi" | "volume" | "all";

const compactUsd = (value: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);

const number = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 });

function ChartTooltip({ active, payload, label }: Record<string, unknown>) {
  if (!active || !Array.isArray(payload) || payload.length === 0) return null;
  return (
    <div className="chart-tooltip">
      <strong>{String(label)}</strong>
      {payload.map((entry: { dataKey?: string; color?: string; value?: number }) => {
        const labels: Record<string, string> = { profit: "Lucro", roi: "ROI", volume: "Volume" };
        const value =
          entry.dataKey === "profit"
            ? compactUsd(entry.value ?? 0)
            : entry.dataKey === "roi"
              ? `${(entry.value ?? 0).toFixed(1)}%`
              : number.format(entry.value ?? 0);
        return (
          <span key={entry.dataKey} style={{ color: entry.color }}>
            {labels[entry.dataKey ?? ""]}: {value}
          </span>
        );
      })}
    </div>
  );
}

export function PerformanceChart({ data }: { data: ChartPoint[] }) {
  const [view, setView] = useState<View>("all");
  const showProfit = view === "profit" || view === "all";
  const showRoi = view === "roi" || view === "all";
  const showVolume = view === "volume" || view === "all";

  return (
    <section className="performance-chart">
      <div className="performance-chart__header">
        <div>
          <span className="section-label">Análise de performance</span>
          <div className="legend">
            <span className="legend--profit">Lucro acumulado (US$)</span>
            <span className="legend--roi">ROI acumulado (%)</span>
            <span className="legend--volume">Volume acumulado</span>
          </div>
        </div>
        <div className="chart-tabs">
          {(
            [
              ["profit", "Lucro"],
              ["roi", "ROI"],
              ["volume", "Volume"],
              ["all", "Todos"],
            ] as const
          ).map(([key, label]) => (
            <button
              type="button"
              key={key}
              className={view === key ? "is-active" : ""}
              onClick={() => setView(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="chart-canvas">
        {data.length === 0 ? (
          <div className="empty-state">Nenhum dado encontrado no período selecionado.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 16, right: 12, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="profitFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00d8b4" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#00d8b4" stopOpacity={0.015} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#16242d" vertical={false} />
              <XAxis
                dataKey="label"
                stroke="#718393"
                tickLine={false}
                axisLine={false}
                minTickGap={44}
                fontSize={11}
              />
              {showProfit && (
                <YAxis
                  yAxisId="profit"
                  stroke="#00d8b4"
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={compactUsd}
                  width={62}
                  fontSize={11}
                />
              )}
              {showRoi && (
                <YAxis
                  yAxisId="roi"
                  orientation="right"
                  stroke="#3b82f6"
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `${Math.round(value)}%`}
                  width={48}
                  fontSize={11}
                />
              )}
              {showVolume && !showRoi && (
                <YAxis
                  yAxisId="volume"
                  orientation="right"
                  stroke="#f5be00"
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => number.format(value)}
                  width={48}
                  fontSize={11}
                />
              )}
              {showVolume && showRoi && (
                <YAxis yAxisId="volume" hide domain={["auto", "auto"]} />
              )}
              <Tooltip content={<ChartTooltip />} />
              {showProfit && (
                <Area
                  yAxisId="profit"
                  type="monotone"
                  dataKey="profit"
                  stroke="#00d8b4"
                  strokeWidth={2.2}
                  fill="url(#profitFill)"
                  dot={false}
                  isAnimationActive={false}
                />
              )}
              {showRoi && (
                <Line
                  yAxisId="roi"
                  type="monotone"
                  dataKey="roi"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              )}
              {showVolume && (
                <Line
                  yAxisId="volume"
                  type="monotone"
                  dataKey="volume"
                  stroke="#f5be00"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
}
